import type { ITradingProvider } from '@ocktra/shared-types';
import type { Order,Position,Funds,Account } from '@ocktra/shared-types';
import type { ProviderConnection, ApiResponse } from '@ocktra/shared-types';
import { OrderStatus, OrderType } from '@ocktra/shared-types';
import { apiClient } from '../../api';
import { TransactionType, PlaceOrderInput } from '@ocktra/shared-types';
/**
 * Paper Trading Provider
 * Simulates real trading without actual capital at risk
 * Forwards all operations to backend API
 */
export class PaperTradingProvider implements ITradingProvider {
    private connection: ProviderConnection | null = null;

    /**
     * Connect to paper trading
     */
    async connect(credentials: Record<string, string>): Promise<ProviderConnection> {
        try {
            const response = await apiClient.post<ApiResponse<ProviderConnection>>('/provider/connect', {
                providerName: 'papertrade',
                providerType: 'trading_provider',
                credentials
            });

            if (response.success && response.data) {
                this.connection = response.data;
                return this.connection;
            }

            throw new Error('Failed to connect to paper trading provider');
        } catch (error) {
            console.error('[PaperTradingProvider] Connection error:', error);
            throw error;
        }
    }

    updateConnectionDetails(connection: ProviderConnection): void {
        this.connection = connection;
    }

    updateCredentials(credentials: Record<string, string>): void {
        // No credentials needed for paper trading
        console.log("credentials",credentials); 
    }

    /**
     * Disconnect from paper trading
     */
    async disconnect(): Promise<void> {
        if (!this.connection) {
            return;
        }

        try {
            await apiClient.post('/provider/disconnect', {
                connectionId: this.connection.id
            });
            this.connection = null;
        } catch (error) {
            console.error('[PaperTradingProvider] Disconnect error:', error);
            throw error;
        }
    }

    /**
     * Check if connected
     */
    isConnected(): boolean {
        return this.connection !== null && this.connection.status === 'connected';
    }

    

    /**
     * Get account information
     */
    async getAccount(): Promise<Account> {
        this.ensureConnected();

        try {
            const response = await apiClient.post<ApiResponse<any>>('/broker/account', {
                connId: this.connection!.id
            });

            if (response.success && response.data) {
                const accountData = response.data;
                return {
                    accountId: accountData.accountId || '',
                    providerName: 'papertrade',
                    userId: this.connection!.userId,
                    funds: {
                        availableFunds: accountData.availableFunds || 0,
                        utilizedFunds: accountData.utilizedFunds || 0,
                        totalFunds: accountData.totalFunds || 0
                    },
                    marginUsed: accountData.marginUsed || 0,
                    marginAvailable: accountData.marginAvailable || 0,
                    lastUpdated: new Date(accountData.lastUpdated || Date.now())
                };
            }

            throw new Error('Failed to fetch account information');
        } catch (error) {
            console.error('[PaperTradingProvider] Get account error:', error);
            throw error;
        }
    }

    /**
     * Place a new order
     */
    async placeOrder(request: PlaceOrderInput): Promise<Order> {
        this.ensureConnected();

        try {
            const orderInput = {
                connId: this.connection!.id,
                exchange: 'NSE', // Default exchange
                tradingSymbol: request.tradingSymbol,
                transactionType: request.transactionType,
                quantity: request.quantity,
                orderType: request.orderType,
                product: request.product,
                price: request.price,
                triggerPrice: request.triggerPrice,
                validity: 'DAY',
                disclosedQuantity: 0
            };

            const response = await apiClient.post<ApiResponse<{ orderId: string }>>('/broker/order/place', orderInput);

            if (response.success && response.data) {
                // Fetch the created order details
                const ordersResponse = await apiClient.post<ApiResponse<any[]>>('/broker/orders', {
                    connId: this.connection!.id
                });

                if (ordersResponse.success && ordersResponse.data) {
                    const order = ordersResponse.data.find((o: any) => o.orderId === response.data!.orderId);
                    if (order) {
                        return this.mapToOrder(order);
                    }
                }

                // If we can't fetch the order, return a basic one
                return {
                    orderId: response.data.orderId,
                    tradingSymbol: request.tradingSymbol,
                    transactionType: request.transactionType,
                    product: request.product,
                    orderType: request.orderType,
                    quantity: request.quantity,
                    filledQuantity: 0,
                    price: request.price || 0,
                    triggerPrice: request.triggerPrice,
                    status: request.orderType === OrderType.MARKET ? OrderStatus.COMPLETED : OrderStatus.PENDING,
                    orderTimestamp: new Date(),
                    averagePrice: 0,
                    exchange: 'NSE',
                    pendingQuantity: request.quantity
                };
            }

            throw new Error('Failed to place order');
        } catch (error) {
            console.error('[PaperTradingProvider] Place order error:', error);
            throw error;
        }
    }

    /**
     * Cancel an order
     */
    async cancelOrder(orderId: string): Promise<void> {
        this.ensureConnected();

        try {
            const response = await apiClient.delete<ApiResponse>('/broker/order/cancel', {
                data: {
                    connId: this.connection!.id,
                    orderId,
                    variety: 'regular'
                }
            });

            if (!response.success) {
                throw new Error('Failed to cancel order');
            }
        } catch (error) {
            console.error('[PaperTradingProvider] Cancel order error:', error);
            throw error;
        }
    }

    /**
     * Get order details
     */
    async getOrder(orderId: string): Promise<Order> {
        this.ensureConnected();

        try {
            const response = await apiClient.post<ApiResponse<any[]>>('/broker/orders', {
                connId: this.connection!.id,
                orderId
            });

            if (response.success && response.data && response.data.length > 0) {
                return this.mapToOrder(response.data[0]);
            }

            throw new Error(`Order ${orderId} not found`);
        } catch (error) {
            console.error('[PaperTradingProvider] Get order error:', error);
            throw error;
        }
    }

    /**
     * Get all orders, optionally filtered by symbol
     */
    async getOrders(symbol?: string): Promise<Order[]> {
        this.ensureConnected();

        try {
            const response = await apiClient.post<ApiResponse<{orders:Order[]}>>('/broker/orders', {
                connId: this.connection!.id
            });

            if (response.success && response.data) {
                let orders = response.data.orders.map((o: any) => this.mapToOrder(o));

                if (symbol) {
                    orders = orders.filter((order: Order) => order.tradingSymbol === symbol);
                }

                return orders;
            }

            return [];
        } catch (error) {
            console.error('[PaperTradingProvider] Get orders error:', error);
            throw error;
        }
    }

    /**
     * Get all positions
     */
    async getPositions(): Promise<Position[]> {
        this.ensureConnected();

        try {
            const response = await apiClient.post<ApiResponse<{ net: any[]; day: any[] }>>('/broker/positions', {
                connId: this.connection!.id
            });

            if (response.success && response.data) {
                const positions = response.data.net || response.data.day || [];
                return positions.map((p: any) => this.mapToPosition(p));
            }

            return [];
        } catch (error) {
            console.error('[PaperTradingProvider] Get positions error:', error);
            throw error;
        }
    }

    /**
     * Get position for specific symbol
     */
    async getPosition(symbol: string): Promise<Position | null> {
        const positions = await this.getPositions();
        return positions.find(p => p.tradingSymbol === symbol) || null;
    }

    /**
     * Close position for specific symbol
     */
    async closePosition(symbol: string): Promise<void> {
        const position = await this.getPosition(symbol);

        if (!position || position.quantity === 0) {
            throw new Error(`No open position for ${symbol}`);
        }

        // Create a market order to close the position
        // const closeSide = position === 'long' ? TransactionType.SELL : TransactionType.BUY;

        // await this.placeOrder({
        //     connId: this.connection!.id,
        //     tradingSymbol: symbol,
        //     transactionType: closeSide,
        //     orderType: OrderType.MARKET,
        //     quantity: Math.abs(position.quantity)
        // });
    }

    async getFunds(): Promise<Funds> {
        this.ensureConnected();

        try {
            const response = await apiClient.post<ApiResponse<any>>('/broker/funds', {
                connId: this.connection!.id
            });

            if (response.success && response.data) {
                const accountData = response.data;
                return {
                    availableFunds: accountData.availableFunds || 0,
                    utilizedFunds: accountData.utilizedFunds || 0,
                    totalFunds: accountData.totalFunds || 0
                };
            }

            throw new Error('Failed to fetch funds information');
        } catch (error) {
            console.error('[PaperTradingProvider] Get funds error:', error);
            throw error;
        }
    }

    // Helper methods

    private ensureConnected(): void {
        if (!this.connection || this.connection.status !== 'connected') {
            throw new Error('Not connected to paper trading provider');
        }
    }

    private mapToOrder(apiOrder: any): Order {
        return {
            orderId: apiOrder.orderId,
            tradingSymbol: apiOrder.tradingSymbol,
            transactionType: apiOrder.transactionType === 'BUY' ? TransactionType.BUY : TransactionType.SELL,
            orderType: this.mapFromOrderType(apiOrder.orderType),
            quantity: apiOrder.quantity,
            filledQuantity: apiOrder.filledQuantity,
            price: apiOrder.price,
            triggerPrice: apiOrder.triggerPrice,
            status: this.mapOrderStatus(apiOrder.status),
            orderTimestamp: new Date(apiOrder.orderTimestamp),
            averagePrice: apiOrder.averagePrice,
            exchange: apiOrder.exchange,
            pendingQuantity: apiOrder.quantity - apiOrder.filledQuantity,
            product: apiOrder.product
        };
    }

    private mapFromOrderType(type: string): OrderType {
        switch (type) {
            case 'MARKET':
                return OrderType.MARKET;
            case 'LIMIT':
                return OrderType.LIMIT;
            case 'SL':
                return OrderType.STOP_LOSS;
            case 'SL-M':
                return OrderType.STOP_LOSS_MARKET;
            default:
                return OrderType.MARKET;
        }
    }

    private mapOrderStatus(status: string): OrderStatus {
        switch (status.toUpperCase()) {
            case 'PENDING':
                return OrderStatus.PENDING;
            case 'OPEN':
                return OrderStatus.OPEN;
            case 'COMPLETED':
                return OrderStatus.COMPLETED;
            case 'CANCELLED':
                return OrderStatus.CANCELLED;
            case 'REJECTED':
                return OrderStatus.REJECTED;
            default:
                return OrderStatus.PENDING;
        }
    }

    private mapToPosition(apiPosition: any): Position {
        const quantity = apiPosition.quantity || 0;

        return {
            tradingSymbol: apiPosition.tradingSymbol,
            quantity: Math.abs(quantity),
            averagePrice: apiPosition.averagePrice || 0,
            pnl: apiPosition.pnl || 0,
            m2m: apiPosition.m2m || 0,
            buyQuantity: quantity > 0 ? quantity : 0,
            buyPrice: quantity > 0 ? apiPosition.averagePrice || 0 : 0,
            sellQuantity: quantity < 0 ? Math.abs(quantity) : 0,
            sellPrice: quantity < 0 ? apiPosition.averagePrice || 0 : 0,
            product: apiPosition.product || 'INTRADAY',
            overnightQuantity: apiPosition.overnightQuantity || 0
        };
    }
}
